import { useSessionOrNull } from '~/contexts/SessionContext';
import { StructureSetId } from '~/domain/changes/ids';
import { StructureSet } from '~/domain/patient/structureset';

/**
 * Escape hatch that returns the live domain {@link StructureSet} for a
 * given id, bypassing the projection view layer.
 *
 * **Use sparingly.** This re-exposes the domain object to UI code, which
 * defeats the purpose of the projection-based UI/domain boundary. Each
 * call site MUST document why a service method or view projection would
 * be inappropriate. Typical justifications:
 *
 *   - A one-off computation that would clutter a domain service with a
 *     UI-only helper (e.g. an ad-hoc sort key derived from internal SS
 *     state).
 *   - Migration scaffolding: a transitional call site mid-refactor, to
 *     be removed once a proper service method is in place.
 *
 * The canonical paths are:
 *   - Reads → projection views via `useStructureSetDetailView`,
 *     `useStructureSetListView`, etc.
 *   - Mutations → domain services (`StructureSetService`, `RoiService`,
 *     `StructureSetExportService`) keyed by `StructureSetRef` / `RoiRef`.
 *
 * Returns `null` when there is no active patient session or no SS with
 * the given id.
 */
export function useLiveStructureSet(
    id: StructureSetId | undefined
): StructureSet | null {
    const session = useSessionOrNull();
    if (!session || id === undefined) {
        return null;
    }
    return session.patient.getStructureSetById(id) ?? null;
}
